import { CompositeLayerProps, Filter } from '@Map/layers/types';
import type VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';

import { setVectorTileLayerStyleLayerIfChanged } from '@Map/esri/utils/setVectorTileLayerStyleLayerIfChanged';

import LayerBase from './LayerBase';

export default class SelectedLayer extends LayerBase {
	protected _selectedItems: string[] = [];

	constructor(
		layerInfo: CompositeLayerProps,
		vectorTileLayer?: VectorTileLayer,
		composite?: import('./LayerBase').CompositeLike,
	) {
		super(layerInfo, vectorTileLayer, {
			zIndexesPerType: { base: 1000, line: 998 },
			selected: true,
			idPostfix: '-selected',
			composite,
		});
	}

	get selectedItems(): string[] {
		return this._selectedItems;
	}

	set selectedItems(items: string[]) {
		this._selectedItems = items;
		this._resetFilterOnMap();
	}

	static getFilter(selectedItems: string[]): Filter {
		if (selectedItems.length) {
			const allIntegers = selectedItems.every(item => /^\d+$/.test(item));
			if (allIntegers) {
				const numbersAndStrings = [
					...selectedItems.map(item => Number(item)),
					...selectedItems,
				];
				return ['in', 'id', ...numbersAndStrings];
			} else {
				return ['in', 'id', ...selectedItems];
			}
		}
		return ['==', 1, 0];
	}

	protected _getFilter = (): Filter => {
		return SelectedLayer.getFilter(this.selectedItems);
	};

	protected _resetFilterOnMap = (secondLayer = false): void => {
		const vtl = this._vectorTileLayer;
		if (!vtl?.loaded) return;
		const filter = this._getFilter();
		const layerId = secondLayer ? this.secondId : this.id;
		const styleLayer = vtl.getStyleLayer(layerId);
		if (!styleLayer) return;

		const filterChanged =
			JSON.stringify(styleLayer.filter) !== JSON.stringify(filter);
		const visibilityUpdateRequired =
			styleLayer.layout?.visibility !== 'visible' &&
			this._selectedItems.length > 0;

		let next = { ...styleLayer, filter };

		next = {
			...next,
			layout: {
				...(next.layout ?? {}),
				visibility: 'visible',
			},
		};
		setVectorTileLayerStyleLayerIfChanged(vtl, next, {
			source: 'SelectedLayer._resetFilterOnMap',
		});
		// If the VTL itself is hidden (all its layers were toggled off), make it
		// visible so the selected feature can render.
		if (this._selectedItems.length > 0 && !vtl.visible) {
			vtl.visible = true;
		}
	};
}
